import React, { FC, useCallback, useEffect, useState } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { pricingUsageRateEndpoint, pricingUsageRatesEndpoint } from "../../../api/endpoints";
import { usePricing } from "./PricingContext";
import { DotLottleLoader } from "../../../components/Loader";
import { formatCurrency } from "../../../utils/formatCurrency";
import type { UsageRate } from "../../../types/pricingPlans";
import { parseNumberInput } from "./inlinePatch";

const money4 = (n: number) => `$${n.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")}`;

// The usage rate card. Our modeled cost per unit × a safety buffer ÷
// (1 − target margin) = the customer unit price shown on Brand Usage.
// Example: $0.03 × 2 ÷ 0.5 = $0.12 per PDP audit.
export const UsageRatesTab: FC = () => {
    const { canEdit } = usePricing();
    const [rates, setRates] = useState<UsageRate[] | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const { data } = await axiosInstant.get(pricingUsageRatesEndpoint);
            setRates(data.rates || []);
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to load usage rates");
            setRates([]);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (!rates) return <DotLottleLoader />;

    const handlePatch = async (kind: string, patch: Partial<UsageRate>) => {
        setBusy(kind);
        try {
            await axiosInstant.patch(pricingUsageRateEndpoint(kind), patch);
            toast.success("Saved");
            await load();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to save");
        } finally {
            setBusy(null);
        }
    };

    const patchNumber = (
        rate: UsageRate,
        field: "unit_cost" | "buffer_multiplier" | "gross_margin",
        raw: string,
        input?: HTMLInputElement | HTMLTextAreaElement | null
    ) => {
        const parsed = parseNumberInput(raw, rate[field], {}, input);
        if ("skip" in parsed || parsed.value === null) return;
        handlePatch(rate.kind, { [field]: parsed.value });
    };

    return (
        <Box className="flex flex-col gap-4">
            <Box className="rounded-lg p-3 bg-tint text-[13px] text-text-1">
                Our cost per unit, a safety buffer, and the target gross margin produce the customer unit price used on
                the <b>Brand Usage</b> page: <span className="font-mono">unit cost × buffer ÷ (1 − margin)</span>. Rates
                apply at read time, so an edit re-prices every period.
            </Box>
            <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid var(--border)" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Usage line</TableCell>
                            <TableCell>Unit cost (ours)</TableCell>
                            <TableCell>Buffer ×</TableCell>
                            <TableCell>Gross margin</TableCell>
                            <TableCell>Buffered cost</TableCell>
                            <TableCell>Customer unit price</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rates.map((r) => (
                            <TableRow key={r.kind}>
                                <TableCell>
                                    <div className="font-medium">{r.label}</div>
                                    <div className="font-mono text-[11px] text-text-2">{r.kind}</div>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        defaultValue={r.unit_cost}
                                        disabled={!canEdit || busy === r.kind}
                                        onBlur={(e) => patchNumber(r, "unit_cost", e.target.value, e.target)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0.01, step: 0.05 }}
                                        defaultValue={r.buffer_multiplier}
                                        disabled={!canEdit || busy === r.kind}
                                        onBlur={(e) => patchNumber(r, "buffer_multiplier", e.target.value, e.target)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0, max: 0.99, step: 0.05 }}
                                        defaultValue={r.gross_margin}
                                        disabled={!canEdit || busy === r.kind}
                                        helperText={`${Math.round(r.gross_margin * 100)}%`}
                                        onBlur={(e) => patchNumber(r, "gross_margin", e.target.value, e.target)}
                                    />
                                </TableCell>
                                <TableCell className="font-mono">{money4(r.buffered_unit_cost)}</TableCell>
                                <TableCell className="font-mono font-semibold">
                                    {r.unit_price >= 1 ? formatCurrency(r.unit_price) : money4(r.unit_price)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
